import {
    Configuration,
    InlineResponse2006,
    ListingApi,
    ListingApiListingAvailabilityRequest,
    ListingType,
    ReadOnlyListing
} from 'hotdesk_typescript';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';
import { ListingTypeKey } from '../enum/listing-type-key';
import {
    listingDedupeKey,
    listingMatchesAmenityFilter,
    parseAmenityFilterIds,
    uniqueWorkspaceListings
} from '@/hotdesk-ui/utils/amenities';

export class MyListing extends ReadOnlyListing {
    displayImage?: Promise<string>;
    listingTypeName?: string;
    displayStatus?: string;
    capacity?: number;
}

type ListingListFilters = Record<string, unknown> & {
    amenitiesIdIn?: string;
    loc?: string;
    country?: string;
    city?: string;
    countryCode?: string;
    region?: string;
    /** `loc` to retry with when a `region` search comes back empty. */
    regionFallbackLoc?: string;
    status?: string;
    limit?: number;
    offset?: number;
    distinct?: string;
};

type ListingListRequest = Parameters<ListingApi['listingList']>[0];

/** Max same-location rows to scan when merging Regus workspace amenities. */
const REGUS_AMENITY_SCAN_MAX = 300;
const REGUS_AMENITY_SCAN_MIN = 120;
/**
 * `distinct=workspace` still reports listing `count`. Cap how many listing
 * rows we fetch to unique-count workspaces for the filter button / heading.
 */
const WORKSPACE_COUNT_SCAN_MAX = 300;

export class ListingService {
    private listingApi!: ListingApi;

    constructor(apiConfig: Configuration) {
        this.listingApi = new ListingApi(apiConfig);
    }

    async getallListings(filters = {}): Promise<InlineResponse2006> {
        const { regionFallbackLoc, ...listFilters } =
            filters as ListingListFilters;
        const res = await this.fetchListings(listFilters);
        // The API matches states by name, and a few Google names have no
        // GeoNames region to match: Irish counties (GeoNames only has the
        // provinces), Taipei, Belgrade. Rather than an empty page, search the
        // circle around the state's centre, as before state search existed.
        if (listFilters.region && regionFallbackLoc && !Number(res.count)) {
            const radiusFilters = { ...listFilters, loc: regionFallbackLoc };
            delete radiusFilters.countryCode;
            delete radiusFilters.region;
            return this.fetchListings(radiusFilters);
        }
        return res;
    }

    private async fetchListings(
        listFilters: ListingListFilters
    ): Promise<InlineResponse2006> {
        try {
            const amenityIds = parseAmenityFilterIds(listFilters.amenitiesIdIn);
            // `countryCode` scopes a search just as much as `country` does —
            // leaving it out meant a country-wide search fell through to the
            // plain server filter, which only knows about listing amenities.
            // Regus/IWG hang theirs off the workspace, so a country whose
            // inventory is all Regus (Turkey) matched nothing at all.
            const hasGeo =
                !!listFilters.loc ||
                !!listFilters.country ||
                !!listFilters.countryCode ||
                !!listFilters.city;

            if (amenityIds.length && hasGeo) {
                return await this.fetchAmenityFilteredPage(
                    listFilters,
                    amenityIds
                );
            }

            const res = await this.listingApi.listingList(
                listFilters as ListingListRequest
            );
            return await this.withDistinctWorkspaceCount(listFilters, res);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    /**
     * Location-scoped amenity pages:
     * 1) server `amenities__id__in` (Hotdesk listing amenities)
     * 2) same loc without that filter, keep Regus/IWG via workspace amenities
     * Then paginate the merged unique set so "Show more" works.
     *
     * (1) is best-effort, and (2) alone still answers correctly — it is the
     * half that finds provider-backed spaces, which is most of what a
     * country-wide search returns. Both queries used to be awaited together,
     * so when the API 500'd on `distinct=workspace` + `amenities__id__in`
     * (backend !1599) one failure took the whole page down instead of
     * degrading to the half that had the answer. That crash is fixed, so this
     * should no longer fire; it stays because a transient failure in one of
     * two parallel queries still shouldn't blank the results, and the log
     * keeps a real regression visible rather than silently swallowed.
     */
    private async fetchAmenityFilteredPage(
        filters: ListingListFilters,
        amenityIds: number[]
    ): Promise<InlineResponse2006> {
        const pageLimit = Number(filters.limit ?? 20) || 20;
        const offset = Number(filters.offset ?? 0) || 0;
        const distinctWorkspace = filters.distinct === 'workspace';
        const { amenitiesIdIn: _omit, ...scopedWithoutAmenity } = filters;

        const scanLimit = Math.min(
            REGUS_AMENITY_SCAN_MAX,
            Math.max(
                REGUS_AMENITY_SCAN_MIN,
                offset + pageLimit * 8,
                pageLimit * 10
            )
        );

        const [serverMatched, localScan] = await Promise.all([
            this.listingApi
                .listingList({
                    ...(filters as ListingListRequest),
                    offset: 0,
                    limit: scanLimit
                })
                .catch((error) => {
                    console.error(
                        'Server amenity filter failed; falling back to the local scan',
                        error
                    );
                    return null;
                }),
            this.listingApi.listingList({
                ...(scopedWithoutAmenity as ListingListRequest),
                offset: 0,
                limit: scanLimit
            })
        ]);

        const localMatches = (
            (localScan.results ?? []) as ReadOnlyListing[]
        ).filter((listing) =>
            listingMatchesAmenityFilter(listing, amenityIds)
        );

        const seen = new Set<string | number>();
        const merged: ReadOnlyListing[] = [];

        for (const listing of [
            ...((serverMatched?.results ?? []) as ReadOnlyListing[]),
            ...localMatches
        ]) {
            const key =
                listingDedupeKey(listing, distinctWorkspace) ??
                `row-${merged.length}`;
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(listing);
        }

        const page = merged.slice(offset, offset + pageLimit);
        // Workspace mode is one card per building; don't use the listing
        // `count` from the server amenity query. Listing mode still prefers
        // the larger of server amenity count vs merged local matches so Show
        // more stays accurate enough without inventing out-of-location rows.
        const scannedPastWindow = merged.length >= scanLimit;
        const count = distinctWorkspace
            ? scannedPastWindow
                ? merged.length + pageLimit
                : merged.length
            : Math.max(
                  serverMatched?.count ?? 0,
                  merged.length,
                  scannedPastWindow ? merged.length + pageLimit : merged.length
              );

        return {
            // Shape comes from whichever response we actually have.
            ...(serverMatched ?? localScan),
            results: page,
            count
        } as InlineResponse2006;
    }

    /**
     * `distinct=workspace` collapses rows to one listing per building, but
     * the API `count` is still the listing total. Explore then shows
     * "Show Results (16)" for 12 workspace cards. When we have (or can
     * fetch) every matching listing row, replace `count` with unique
     * workspaces so the button, heading, and grid agree.
     */
    private async withDistinctWorkspaceCount(
        filters: ListingListFilters,
        res: InlineResponse2006
    ): Promise<InlineResponse2006> {
        if (filters.distinct !== 'workspace') {
            return res;
        }

        const pageLimit = Number(filters.limit ?? 20) || 20;
        const offset = Number(filters.offset ?? 0) || 0;
        const apiCount = Number(res.count ?? 0);
        const pageResults = (res.results ?? []) as ReadOnlyListing[];
        const pageUnique = uniqueWorkspaceListings(pageResults);

        const haveAllListingRows =
            apiCount === 0 ||
            (offset === 0 &&
                (pageLimit >= apiCount || pageResults.length >= apiCount));

        if (haveAllListingRows) {
            return {
                ...res,
                results: pageUnique,
                count: pageUnique.length
            };
        }

        if (
            offset === 0 &&
            apiCount > 0 &&
            apiCount <= WORKSPACE_COUNT_SCAN_MAX
        ) {
            try {
                const full = await this.listingApi.listingList({
                    ...(filters as ListingListRequest),
                    offset: 0,
                    limit: apiCount
                });
                const allUnique = uniqueWorkspaceListings(
                    (full.results ?? []) as ReadOnlyListing[]
                );
                return {
                    ...res,
                    results: pageUnique,
                    count: allUnique.length
                };
            } catch (error) {
                console.error(
                    'Workspace distinct count scan failed; using listing count',
                    error
                );
            }
        }

        return { ...res, results: pageUnique };
    }

    getListingsDataParams(
        type: string,
        limit: number,
        offset: number = 0,
        searchLocation?: { lat: number; lng: number; radius: number } | null,
        filters?: {},
        country?: string,
        city?: string,
        subscriptionIdIn?: string,
        countryCode?: string,
        /**
         * State name for a state-wide search; only sent with `countryCode`.
         * `searchLocation` then becomes the radius search to fall back on.
         */
        region?: string
    ) {
        let params: any;
        /* eslint-disable @typescript-eslint/no-explicit-any */
        // Country-wide search: filter by ISO code instead of a lat/lng radius —
        // a 15km circle around a country's centroid usually contains nothing.
        // A state works the same way, narrowed by its name within the country.
        if (!!countryCode) {
            params = {
                countryCode,
                ...(region ? { region } : {}),
                ...(region && searchLocation
                    ? {
                          regionFallbackLoc: [
                              searchLocation.lat,
                              searchLocation.lng,
                              searchLocation.radius
                          ].toString()
                      }
                    : {}),
                status: '1',
                limit,
                offset,
                subscriptionIdIn,
                ...filters
            };
        } else if (!!searchLocation) {
            params = {
                loc: [
                    searchLocation.lat,
                    searchLocation.lng,
                    searchLocation.radius
                ].toString(),
                status: '1',
                limit,
                offset,
                subscriptionIdIn,
                ...filters
            };
        } else if (!!country && !!city) {
            params = {
                country: country,
                city: city,
                status: '1',
                limit,
                offset,
                subscriptionIdIn,
                ...filters
            };
        } else if (!!country) {
            params = {
                country: country,
                status: '1',
                limit,
                offset,
                subscriptionIdIn,
                ...filters
            };
        }

        if (type === Carousel.workspace) {
            params.workspaceStatus = 1;
            params.distinct = 'workspace';
        }
        /* eslint-disable @typescript-eslint/no-explicit-any */

        return params;
    }

    getListingType(
        listingType: ListingType[],
        listingTypeId: number | undefined
    ): ListingType {
        return listingType.find((item) => {
            return item.id === listingTypeId;
        }) as ListingType;
    }

    updateListingData(
        listingType: ListingType[],
        listing: ReadOnlyListing[]
    ): MyListing[] {
        return listing.map((list) => {
            return {
                ...list,
                listingTypeName: this.getListingType(listingType, list.type)
                    .type,
                capacity:
                    list.type === ListingTypeKey.DESK
                        ? list.rules?.quantity
                        : list.rules?.capacity
            };
        });
    }

    getListingAvailability(param: ListingApiListingAvailabilityRequest) {
        return this.listingApi.listingAvailability({ ...param });
    }
}
